import { LevelCatalog, Pets, petSheet } from "@highmarch/app";
import { sheetHeadHtml } from "../components";
import type { UpgradeAction } from "../hub/queueModel";
import { econCue } from "../hub/purchaseFx";
import { refusalText } from "../hub/shopActions";
import { skinCards } from "../hub/skinsModel";
import { skinsLink } from "../hub/skinsLink";
import { toast } from "../toast";
import { petLink } from "./petLink";
import { abilitiesHtml, coatsHtml, petHeadPortrait, petHeadSub, petNextHtml, petStageHtml } from "./petSheetUi";
import { coatWords, equipToast } from "./petWords";

/** A sheet's own parts inside the upgrade sheet (hub/upgradeSheet.ts): its head, body, next level and binder. */
export interface PetSheetPart {
  readonly head: string;
  readonly body: string;
  readonly next: string;
  readonly foot?: string;
  bind?(sheet: HTMLElement): void;
}

/**
 * The pet sheet's part (Pets v2, docs/PETS.md) for the pet at roster index `index`: the head (its portrait in its coat,
 * "Lv 7 / 12 · up to your best tower's level"), the stage with the come-along switch, the abilities, the coats and the
 * next level's button (`action`: queueModel.ts upgradeAction). The switch and a caught coat act here and call
 * `changed` (the hub saves, redraws, and this sheet re-opens); the upgrade buttons are the upgrade sheet's own.
 */
export function petSheetPart(catalog: LevelCatalog, index: number, action: UpgradeAction, secondary: boolean, hint: string, changed: () => void): PetSheetPart {
  const B = catalog.balance, p = catalog.profile, pets = new Pets(catalog), pet = B.pets.list[index];
  const s = petSheet(B, p, pet, pets.equipped()?.id ?? null);
  const link = skinsLink();
  const cards = link ? skinCards(link.cosmetics, "pet", pet.id, pet.name, link.now()) : [];
  const seconds = action.kind === "upgrade" || action.kind === "queue" ? p.durationFor(action.toLevel) : 0;
  return {
    head: sheetHeadHtml({ title: pet.name, sub: petHeadSub(s), portrait: petHeadPortrait(s), tint: "blue" }),
    body: `${petStageHtml(s)}${abilitiesHtml(s)}${coatsHtml(cards)}`,
    next: petNextHtml(s, action, seconds, secondary, hint),
    bind: (sheet) => {
      const parent = sheet.parentElement ?? document.body;
      sheet.querySelector<HTMLButtonElement>(".pa-come")?.addEventListener("click", () => {
        if (!pets.equip(s.equipped ? null : pet.id)) return;
        econCue("tap");
        petLink.changed();                                     // the lawn and the battles wear the choice at once
        toast(sheet.parentElement ?? document.body, equipToast(pet, s.equipped ? null : pets.card(pet)));
        changed();
      });
      for (const b of sheet.querySelectorAll<HTMLButtonElement>(".pa-coat")) b.onclick = () => {
        const c = cards.find((x) => (x.id ?? "") === b.dataset.id);
        if (!c || !link || c.worn) return;
        if (!c.access.have) { toast(parent, coatWords(false, false)); return; }
        const r = link.cosmetics.equip("pet", c.id, pet.id);
        if (r !== true) { toast(parent, refusalText(r)); return; }
        econCue("tap");
        link.changed();
        toast(parent, c.id ? `${c.look.name} on your ${pet.name}` : `${pet.name} back in its own coat`);
        changed();
      };
    },
  };
}
